import axios from 'axios';

type TransactionSimulationErrorBody = {
  cause?: unknown;
};

type TransactionSimulationErrorResponse = {
  error?: {
    body?: TransactionSimulationErrorBody;
  };
};

type TransactionSimulationRequestFailure = {
  message: string;
  retryable: boolean;
};

const MOBILE_NOT_CONNECTED_MESSAGE =
  'Connect your mobile device before simulating this transaction.';

const CAUSE_MESSAGES: Readonly<Record<string, string>> = {
  mobile_not_connected: MOBILE_NOT_CONNECTED_MESSAGE,
  transaction_simulation_unavailable_on_anvil:
    'Transaction simulation is unavailable for local Smaug transactions.',
  transaction_simulation_unsupported_network:
    'Transaction simulation is unavailable on this network.',
};

/** Returns the original string when the body owns a non-blank `cause`. */
function errorCause(
  body: TransactionSimulationErrorBody | undefined
): string | undefined {
  // Case: The response body is absent or does not own a cause field.
  if (!body || !Object.prototype.hasOwnProperty.call(body, 'cause')) {
    return undefined;
  }

  // Case: Non-string or blank causes are invalid.
  return typeof body.cause === 'string' && body.cause.trim().length > 0
    ? body.cause
    : undefined;
}

/** Resolves a non-blank cause only when it names an own, non-empty map entry. */
function causeMessage(cause: unknown): string | undefined {
  // Case: Unknown, blank, or unsafe causes must not become user-facing messages.
  if (
    typeof cause !== 'string' ||
    cause.trim().length === 0 ||
    !Object.prototype.hasOwnProperty.call(CAUSE_MESSAGES, cause)
  ) {
    return undefined;
  }

  const message = CAUSE_MESSAGES[cause];
  return typeof message === 'string' && message.length > 0
    ? message
    : undefined;
}

/**
 * Converts transport and Den API failures into the retry behavior rendered by
 * the sheet. Domain failures are deterministic; absent responses and server
 * errors may succeed on a later attempt.
 */
export function toTransactionSimulationRequestFailure(
  error: unknown
): TransactionSimulationRequestFailure {
  // Case: Transport failures without an HTTP response may succeed on retry.
  if (!axios.isAxiosError(error) || !error.response) {
    return {
      message: 'Unable to reach Den. Please try again.',
      retryable: true,
    };
  }

  const status = error.response.status;
  const body = (
    error.response.data as TransactionSimulationErrorResponse | undefined
  )?.error?.body;
  const cause = errorCause(body);

  // Case: Server, timeout, and throttling responses are retryable.
  if (status >= 500 || status === 408 || status === 429) {
    return {
      message: 'Transaction simulation is temporarily unavailable.',
      retryable: true,
    };
  }

  // Case: An expired session requires the user to sign in again.
  if (status === 401) {
    return {
      message:
        'Your session has expired. Sign in again to simulate this transaction.',
      retryable: false,
    };
  }

  // Case: A disconnected mobile device can be connected before retrying.
  if (cause === 'mobile_not_connected') {
    return {
      message: MOBILE_NOT_CONNECTED_MESSAGE,
      retryable: true,
    };
  }

  // Case: A forbidden response cannot be retried by this user.
  if (status === 403) {
    return {
      message: 'You do not have permission to simulate this transaction.',
      retryable: false,
    };
  }

  return {
    message:
      causeMessage(cause) ??
      'This transaction cannot be simulated. Check its details and try again.',
    retryable: false,
  };
}
